'use client'
import { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { useTranslations, useLocale } from 'next-intl'
import Link from 'next/link'
import { Upload, FileText, Eye } from 'lucide-react'
import { exampleMarkdown } from '@/constants/example-markdown'
import { emojifyText } from '@/lib/emoji'
import { renderMermaidToPng } from '@/lib/mermaid'
import {
  MarkdownConverterShell,
  useMarkdownConverterCommon,
} from '@/components/markdown-converter-shell'
import { trackGtagEvent } from '@/lib/gtag'
import { trackPlausibleEvent } from '@/lib/plausible'
import {
  splitMathSegments,
  type MathSplit,
} from '@/lib/markdown/math-delimiters'
import type {
  MarkdownAstNode,
  ParsedMarkdownAst,
} from '@/lib/markdown/parse-markdown-ast'
import { renderLatexToPng } from '@/lib/math-render'
import InAppDownloadModal from '@/components/in-app-download-modal'
import { useInAppDownloadGuard } from '@/lib/use-in-app-download-guard'
import { DocumentExportSettingsButton } from '@/components/document-export-settings-button'
import {
  DEFAULT_DOCUMENT_EXPORT_SETTINGS,
  type DocumentExportSettings,
  type ExportTextDirection,
} from '@/lib/document-export-settings'
import { getWordExportFileName } from '@/lib/word-export-file-name'
import { loadWordImage } from '@/lib/word-image'
import { createWordNavigation } from '@/lib/word-navigation'
import {
  prepareWordReferences,
  isSafeWordHyperlink,
} from '@/lib/markdown/word-references'
import {
  getWordTableAlignment,
  type WordTableColumnAlignment,
} from '@/lib/markdown/word-table-alignment'

const BLANK_LINE_SPACING = 200 // twips, ≈10pt, to keep markdown blank lines visible
const TABLE_BLOCK_SPACING = 240 // twips, ~12pt, to separate tables from text

const LazyMarkdownFullscreenViewer = dynamic(
  () => import('@/components/markdown-fullscreen-viewer'),
  { ssr: false, loading: () => null },
)

type ConversionToolkit = {
  docx: typeof import('docx')
  parseMarkdownAst: (markdown: string) => Promise<ParsedMarkdownAst>
  saveAs: (typeof import('file-saver'))['saveAs']
}

let conversionToolkitPromise: Promise<ConversionToolkit> | null = null
type MathmlConverterModule = typeof import('@seewo-doc/mathml2omml')
type KatexModule = typeof import('katex')
let mathmlConverterPromise: Promise<MathmlConverterModule | null> | null = null
let katexPromise: Promise<KatexModule | null> | null = null
const runWhenIdle = (fn: () => void) => {
  if (typeof window === 'undefined') return
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void) => void)
    | undefined
  if (ric) {
    ric(fn)
    return
  }
  setTimeout(fn, 0)
}

const yieldToBrowser = () =>
  new Promise<void>((resolve) => setTimeout(resolve, 0))
const CONTENT_WIDTH_PX = Math.round(6.5 * 96)
const CONTENT_WIDTH_TWIPS = Math.round(6.5 * 1440)
const INLINE_MATH_MAX_WIDTH = Math.round(CONTENT_WIDTH_PX * 0.6)
const DISPLAY_MATH_MAX_WIDTH = Math.round(CONTENT_WIDTH_PX * 0.9)
const LIST_INDENT_BASE_TWIPS = 20
const LIST_INDENT_STEP_TWIPS = 420
const LIST_BULLET_NUMBERING_REFERENCE = 'markdown-bullet-numbering'
const LIST_CHECKED_NUMBERING_REFERENCE = 'markdown-task-checked-numbering'
const LIST_UNCHECKED_NUMBERING_REFERENCE = 'markdown-task-unchecked-numbering'
const LIST_BULLET_TEXT_INDENT_BASE_TWIPS = 360
const LIST_BULLET_HANGING_TWIPS = 360
const BLOCKQUOTE_INDENT_TWIPS = 300
const HEADING_FONT_SIZE_MULTIPLIERS = {
  heading1: 5 / 3,
  heading2: 3 / 2,
  heading3: 4 / 3,
  heading4: 7 / 6,
  heading5: 1,
  heading6: 5 / 6,
}

type TextDirection = 'ltr' | 'rtl'

const RTL_CODE_POINT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0590, 0x05ff], // Hebrew
  [0x0600, 0x06ff], // Arabic
  [0x0700, 0x074f], // Syriac
  [0x0750, 0x077f], // Arabic Supplement
  [0x0780, 0x07bf], // Thaana
  [0x07c0, 0x07ff], // NKo
  [0x0800, 0x083f], // Samaritan
  [0x0840, 0x085f], // Mandaic
  [0x0870, 0x089f], // Arabic Extended-B
  [0x08a0, 0x08ff], // Arabic Extended-A
  [0xfb1d, 0xfdff], // Hebrew and Arabic presentation forms
  [0xfe70, 0xfeff], // Arabic Presentation Forms-B
  [0x10800, 0x10fff], // RTL historic scripts
  [0x1e800, 0x1e95f], // Mende Kikakui, Adlam
]

const LTR_CODE_POINT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0041, 0x005a], // Basic Latin uppercase
  [0x0061, 0x007a], // Basic Latin lowercase
  [0x00c0, 0x02af], // Latin extensions and IPA
  [0x0370, 0x052f], // Greek and Cyrillic
  [0x0530, 0x058f], // Armenian
  [0x0900, 0x0dff], // Indic scripts
  [0x0e00, 0x0eff], // Thai and Lao
  [0x10a0, 0x10ff], // Georgian
  [0x1100, 0x11ff], // Hangul Jamo
  [0x1c90, 0x1cbf], // Georgian Extended
  [0x3040, 0x30ff], // Hiragana and Katakana
  [0x3100, 0x312f], // Bopomofo
  [0x31a0, 0x31bf], // Bopomofo Extended
  [0x3400, 0x9fff], // CJK ideographs
  [0xac00, 0xd7af], // Hangul syllables
  [0xf900, 0xfaff], // CJK compatibility ideographs
  [0xff21, 0xff3a], // Fullwidth Latin uppercase
  [0xff41, 0xff5a], // Fullwidth Latin lowercase
]

const isRtlCodePoint = (codePoint: number) =>
  RTL_CODE_POINT_RANGES.some(
    ([start, end]) => codePoint >= start && codePoint <= end,
  )

const isLtrCodePoint = (codePoint: number) =>
  LTR_CODE_POINT_RANGES.some(
    ([start, end]) => codePoint >= start && codePoint <= end,
  )

const getFirstStrongTextDirection = (value?: string): TextDirection | null => {
  if (!value) return null

  for (const char of value) {
    const codePoint = char.codePointAt(0)
    if (codePoint === undefined) continue
    if (isRtlCodePoint(codePoint)) return 'rtl'
    if (isLtrCodePoint(codePoint)) return 'ltr'
  }

  return null
}

const resolveTextDirection = (
  value?: string,
  fallback: TextDirection = 'ltr',
): TextDirection => getFirstStrongTextDirection(value) ?? fallback

const resolveExportTextDirection = (
  value: string | undefined,
  configuredDirection: ExportTextDirection,
  fallback: TextDirection = 'ltr',
): TextDirection =>
  configuredDirection === 'auto'
    ? resolveTextDirection(value, fallback)
    : configuredDirection

const toHalfPoints = (fontSizePt: number) => Math.round(fontSizePt * 2)

const getHeadingRunSize = (
  baseFontSizePt: number,
  heading: keyof typeof HEADING_FONT_SIZE_MULTIPLIERS,
) => toHalfPoints(baseFontSizePt * HEADING_FONT_SIZE_MULTIPLIERS[heading])

type WordMarkdownToken = {
  type: string
  text?: string
  raw?: string
  tokens?: WordMarkdownToken[]
  depth?: number
  ordered?: boolean
  start?: number | null
  checked?: boolean | null
  lang?: string
  href?: string
  bookmark?: string
  noteId?: number
  referenceBookmark?: string
  display?: boolean
  items?: WordMarkdownToken[]
  header?: WordMarkdownToken[]
  rows?: WordMarkdownToken[][]
  align?: WordTableColumnAlignment[]
}

const getAstSource = (node: MarkdownAstNode, markdown: string) => {
  const start = node.position?.start?.offset
  const end = node.position?.end?.offset
  if (typeof start !== 'number' || typeof end !== 'number') return ''
  return markdown.slice(start, end)
}

const getAstNodeText = (node: MarkdownAstNode): string => {
  if (node.type === 'break') return '\n'
  if (node.type === 'image') return node.alt ?? ''
  if (typeof node.value === 'string') return node.value
  if (!Array.isArray(node.children)) return ''
  return node.children.map((child) => getAstNodeText(child)).join('')
}

// 复用 GFM 的节点坐标，避免首个格式节点前残留已识别的任务标记。
const getListItemBlockText = (
  node: MarkdownAstNode,
  markdown: string,
  stripTaskMarker = false,
) => {
  let source = getAstSource(node, markdown)
  const start = node.position?.start?.offset
  const firstInlineStart = node.children?.[0]?.position?.start?.offset
  if (
    stripTaskMarker &&
    node.type === 'paragraph' &&
    typeof start === 'number' &&
    typeof firstInlineStart === 'number' &&
    /^\[[ xX]\][ \t\r\n>]+$/.test(markdown.slice(start, firstInlineStart))
  ) {
    source = source.slice(firstInlineStart - start)
  }
  return source || getAstNodeText(node)
}

const adaptInlineAstNode = (
  node: MarkdownAstNode,
  markdown: string,
): WordMarkdownToken => {
  const childTokens = Array.isArray(node.children)
    ? node.children.map((child) => adaptInlineAstNode(child, markdown))
    : undefined
  const text = getAstNodeText(node)

  switch (node.type) {
    case 'wordBookmark':
      return {
        type: 'bookmark',
        bookmark: String(node.bookmark),
        text,
        tokens: childTokens,
      }
    case 'wordFootnoteReference':
      return {
        type: 'footnote',
        noteId: Number(node.noteId),
        referenceBookmark:
          typeof node.referenceBookmark === 'string'
            ? node.referenceBookmark
            : undefined,
      }
    case 'text':
      return { type: 'text', text }
    case 'strong':
      return { type: 'strong', text, tokens: childTokens }
    case 'emphasis':
      return { type: 'em', text, tokens: childTokens }
    case 'delete':
      return { type: 'del', text, tokens: childTokens }
    case 'inlineCode':
      return { type: 'codespan', text }
    case 'link':
      return {
        type: 'link',
        text,
        href: typeof node.url === 'string' ? node.url : '',
        bookmark: typeof node.bookmark === 'string' ? node.bookmark : undefined,
        tokens: childTokens,
      }
    case 'image':
      return {
        type: 'image',
        text: node.alt ?? '',
        href: typeof node.url === 'string' ? node.url : '',
      }
    case 'break':
      return { type: 'br' }
    case 'inlineMath': {
      const raw = getAstSource(node, markdown).trimStart()
      return {
        type: 'math',
        text,
        display: raw.startsWith('$$') || raw.startsWith('\\['),
      }
    }
    case 'html':
      return { type: 'html', text }
    default:
      return {
        type: node.type,
        text,
        tokens: childTokens,
      }
  }
}

const adaptBlockAstNode = (
  node: MarkdownAstNode,
  markdown: string,
): WordMarkdownToken => {
  switch (node.type) {
    case 'heading':
      return {
        type: 'heading',
        depth: typeof node.depth === 'number' ? node.depth : 1,
        text: getAstNodeText(node),
        tokens: node.children?.map((child) =>
          adaptInlineAstNode(child, markdown),
        ),
      }
    case 'paragraph':
      return {
        type: 'paragraph',
        text: getAstSource(node, markdown) || getAstNodeText(node),
        tokens: node.children?.map((child) =>
          adaptInlineAstNode(child, markdown),
        ),
      }
    case 'math':
      return { type: 'math', text: node.value ?? '', display: true }
    case 'html':
      return { type: 'html', text: node.value ?? '', raw: node.value ?? '' }
    case 'code':
      return {
        type: 'code',
        text: node.value ?? '',
        lang: typeof node.lang === 'string' ? node.lang : '',
      }
    case 'thematicBreak':
      return { type: 'hr' }
    case 'blockquote':
      return {
        type: 'blockquote',
        text: getAstNodeText(node),
        tokens: adaptBlockAstNodes(node.children ?? [], markdown),
      }
    case 'list':
      return {
        type: 'list',
        ordered: Boolean(node.ordered),
        start: typeof node.start === 'number' ? node.start : null,
        items: (node.children ?? []).map((item) => {
          const itemChildren = item.children ?? []
          const itemBlocks = adaptBlockAstNodes(itemChildren, markdown)
          const isTaskItem = typeof item.checked === 'boolean'
          if (
            itemChildren[0]?.type === 'paragraph' &&
            itemBlocks[0]?.type === 'paragraph'
          ) {
            itemBlocks[0].text = getListItemBlockText(
              itemChildren[0],
              markdown,
              isTaskItem,
            )
          }
          return {
            type: 'list_item',
            checked: isTaskItem ? item.checked : null,
            text: itemChildren
              .filter((child) => child.type !== 'list')
              .map((child, index) =>
                getListItemBlockText(
                  child,
                  markdown,
                  index === 0 && isTaskItem,
                ),
              )
              .join('\n'),
            tokens: itemBlocks,
          }
        }),
      }
    case 'table': {
      const tableRows = node.children ?? []
      const adaptedRows = tableRows.map((row) =>
        (row.children ?? []).map((cell) => ({
          type: 'table_cell',
          text: getAstNodeText(cell),
          tokens: cell.children?.map((child) =>
            adaptInlineAstNode(child, markdown),
          ),
        })),
      )
      return {
        type: 'table',
        header: adaptedRows[0] ?? [],
        rows: adaptedRows.slice(1),
        align: Array.isArray(node.align)
          ? node.align.map((alignment) =>
              alignment === 'left' ||
              alignment === 'center' ||
              alignment === 'right'
                ? alignment
                : null,
            )
          : [],
      }
    }
    default:
      return {
        type: node.type,
        text: getAstNodeText(node),
        tokens: node.children?.map((child) =>
          adaptInlineAstNode(child, markdown),
        ),
      }
  }
}

const adaptBlockAstNodes = (
  nodes: MarkdownAstNode[],
  markdown: string,
): WordMarkdownToken[] => {
  const tokens: WordMarkdownToken[] = []
  let previousNode: MarkdownAstNode | null = null

  for (const node of nodes) {
    if (node.type === 'definition' || node.type === 'footnoteDefinition') {
      previousNode = node
      continue
    }
    const previousEnd = previousNode?.position?.end?.offset
    const currentStart = node.position?.start?.offset
    if (typeof previousEnd === 'number' && typeof currentStart === 'number') {
      const gap = markdown.slice(previousEnd, currentStart)
      const newlineCount = (gap.match(/\n/g) ?? []).length
      const blankLineCount = Math.max(0, newlineCount - 1)
      if (blankLineCount > 0) {
        tokens.push({
          type: 'space',
          raw: '\n'.repeat(blankLineCount + 1),
        })
      }
    }
    tokens.push(adaptBlockAstNode(node, markdown))
    previousNode = node
  }

  return tokens
}

const normalizeKatexMathml = (markup: string) => {
  if (!markup) return markup
  const extracted = markup.match(/<math[\s\S]*<\/math>/i)?.[0]
  // KaTeX 解析失败时会返回 katex-error <span>，不要交给 MathML 转换器。
  if (!extracted) return null
  return extracted.replace(/<annotation[\s\S]*?<\/annotation>/gi, '')
}

const OMML_MATH_NAMESPACE =
  'http://schemas.openxmlformats.org/officeDocument/2006/math'

const normalizeOmmlForWps = (omml: string) => {
  if (
    !omml ||
    typeof DOMParser === 'undefined' ||
    typeof XMLSerializer === 'undefined'
  ) {
    return omml
  }

  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(omml, 'application/xml')
    if (doc.getElementsByTagName('parsererror').length > 0) return omml

    let changed = false
    const groupChrProps = Array.from(
      doc.getElementsByTagNameNS(OMML_MATH_NAMESPACE, 'groupChrPr'),
    )

    groupChrProps.forEach((groupChrPr) => {
      const directChildren = Array.from(groupChrPr.childNodes).filter(
        (node): node is Element => node.nodeType === Node.ELEMENT_NODE,
      )
      const chrNode = directChildren.find((child) => child.localName === 'chr')
      if (!chrNode) return

      const posValue =
        chrNode.getAttributeNS(OMML_MATH_NAMESPACE, 'pos') ??
        chrNode.getAttribute('m:pos') ??
        chrNode.getAttribute('pos')
      if (!posValue) return

      chrNode.removeAttributeNS(OMML_MATH_NAMESPACE, 'pos')
      chrNode.removeAttribute('m:pos')
      chrNode.removeAttribute('pos')

      let posNode = directChildren.find((child) => child.localName === 'pos')
      if (!posNode) {
        posNode = doc.createElementNS(OMML_MATH_NAMESPACE, 'm:pos')
        groupChrPr.appendChild(posNode)
      }
      posNode.setAttribute('m:val', posValue)
      changed = true
    })

    return changed ? new XMLSerializer().serializeToString(doc) : omml
  } catch (err) {
    console.warn('Failed to normalize OMML for WPS', err)
    return omml
  }
}

const hasEmptyNaryOperand = (omml: string) => {
  if (!omml) return false

  if (typeof DOMParser === 'undefined') {
    return /<m:nary\b[^>]*>[\s\S]*?<m:e\b[^>]*\/\s*>[\s\S]*?<\/m:nary>/i.test(
      omml,
    )
  }

  try {
    const doc = new DOMParser().parseFromString(omml, 'application/xml')
    if (doc.getElementsByTagName('parsererror').length > 0) return false

    const naryNodes = Array.from(
      doc.getElementsByTagNameNS(OMML_MATH_NAMESPACE, 'nary'),
    )

    return naryNodes.some((naryNode) => {
      const operand = Array.from(naryNode.childNodes).find(
        (node): node is Element =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node as Element).localName === 'e',
      )
      if (!operand) return true

      return Array.from(operand.childNodes).every((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) return false
        if (
          node.nodeType === Node.TEXT_NODE ||
          node.nodeType === Node.CDATA_SECTION_NODE
        ) {
          return !(node.nodeValue ?? '').trim()
        }
        return true
      })
    })
  } catch (err) {
    console.warn('Failed to inspect OMML n-ary operand', err)
    return false
  }
}

const getWpsUnsafeOmmlReasons = (latex: string, omml: string) => {
  if (!omml) return [] as string[]

  const reasons: string[] = []
  const hasBraceGlyphText = /<m:t\b[^>]*>[^<]*[⏟⏞][^<]*<\/m:t>/i.test(omml)
  const usesDynamicDelimiters = /\\left|\\right/.test(latex)
  const hasDelimiterObject = /<m:d(?:\b|>)|<m:dPr\b/i.test(omml)
  const hasMissingNaryOperand = hasEmptyNaryOperand(omml)

  if (hasBraceGlyphText) {
    reasons.push('brace-glyph-rendered-as-text')
  }
  if (usesDynamicDelimiters && !hasDelimiterObject) {
    reasons.push('missing-omml-delimiter')
  }
  if (hasMissingNaryOperand) {
    reasons.push('empty-nary-operand')
  }

  return reasons
}

const getListIndentLeft = (level: number) =>
  LIST_INDENT_BASE_TWIPS + level * LIST_INDENT_STEP_TWIPS

const getBulletListHanging = (fontSizePt: number) =>
  Math.max(LIST_BULLET_HANGING_TWIPS, Math.round(fontSizePt * 30))

const getBulletListTextIndentLeft = (level: number, fontSizePt = 12) => {
  const markerWidth = getBulletListHanging(fontSizePt)
  return (
    Math.max(LIST_BULLET_TEXT_INDENT_BASE_TWIPS, markerWidth) +
    level * Math.max(LIST_INDENT_STEP_TWIPS, markerWidth)
  )
}

const loadConversionToolkit = async (): Promise<ConversionToolkit> => {
  if (!conversionToolkitPromise) {
    conversionToolkitPromise = Promise.all([
      import('docx'),
      import('@/lib/markdown/parse-markdown-ast'),
      import('file-saver'),
    ]).then(([docx, markdownAstModule, fileSaver]) => {
      const saveAsExport =
        typeof (fileSaver as any).saveAs === 'function'
          ? (fileSaver as any).saveAs
          : typeof (fileSaver as any).default === 'function'
            ? (fileSaver as any).default
            : (fileSaver as any)

      return {
        docx,
        parseMarkdownAst: markdownAstModule.parseMarkdownAst,
        saveAs: saveAsExport,
      }
    })
  }

  return conversionToolkitPromise
}

const loadMathmlConverter = async (): Promise<MathmlConverterModule | null> => {
  if (!mathmlConverterPromise) {
    mathmlConverterPromise = import('@seewo-doc/mathml2omml')
      .then((mod) => mod as MathmlConverterModule)
      .catch((err) => {
        console.warn('Failed to load MathML converter', err)
        return null
      })
  }

  return mathmlConverterPromise
}

const loadKatex = async (): Promise<KatexModule | null> => {
  if (!katexPromise) {
    katexPromise = import('katex')
      .then((mod) => (mod as any).default ?? mod)
      .catch((err) => {
        console.warn('Failed to load KaTeX', err)
        return null
      })
  }

  return katexPromise
}

export default function MarkdownToWordConverter() {
  const t = useTranslations('Converter')
  const tc = useTranslations('common')
  const locale = useLocale()
  const localizedHref =
    locale === 'en'
      ? '/tools/markdown-to-html'
      : `/${locale}/tools/markdown-to-html`
  const {
    info: inAppInfo,
    isOpen: isInAppModalOpen,
    guardDownload,
    closeModal: closeInAppModal,
    continueWithDownload,
  } = useInAppDownloadGuard()
  const {
    markdownContent,
    setMarkdownContent,
    fileName,
    isConverting,
    setIsConverting,
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
    dropzone,
    uploadState,
    sourceScrollRef,
    previewScrollRef,
    handleSourceScroll,
  } = useMarkdownConverterCommon()
  const [isFullscreenReady, setIsFullscreenReady] = useState(false)
  const [documentSettings, setDocumentSettings] =
    useState<DocumentExportSettings>(DEFAULT_DOCUMENT_EXPORT_SETTINGS)

  useEffect(() => {
    if (isFullscreen) {
      setIsFullscreenReady(true)
    }
  }, [isFullscreen])

  useEffect(() => {
    runWhenIdle(() => {
      loadConversionToolkit().catch((err) =>
        console.warn('Preloading DOCX toolkit failed', err),
      )
      loadMathmlConverter().catch((err) =>
        console.warn('Preloading MathML converter failed', err),
      )
      loadKatex().catch((err) => console.warn('Preloading KaTeX failed', err))
    })
  }, [])

  // 解析Markdown并转换为Word文档结构（支持表格、删除线、图片、水平线）
  const parseMarkdownToWordElements = async (
    markdown: string,
    toolkit: ConversionToolkit,
    documentSettings: DocumentExportSettings,
    parsedMarkdownInput?: ParsedMarkdownAst,
    navigation = createWordNavigation(toolkit.docx),
  ) => {
    const {
      Paragraph,
      TextRun,
      ImageRun,
      Table,
      TableRow,
      TableCell,
      WidthType,
      TableLayoutType,
      AlignmentType,
      ShadingType,
      BorderStyle,
      HeadingLevel,
      Bookmark,
      ExternalHyperlink,
      InternalHyperlink,
      TabStopType,
    } = toolkit.docx
    const parsedMarkdown =
      parsedMarkdownInput ?? (await toolkit.parseMarkdownAst(markdown))
    const tokens = adaptBlockAstNodes(
      parsedMarkdown.root.children ?? [],
      parsedMarkdown.markdown,
    )
    const elements: any[] = []
    const resolveTextDirection = (
      value?: string,
      fallback: TextDirection = 'ltr',
    ) =>
      resolveExportTextDirection(
        value,
        documentSettings.textDirection,
        fallback,
      )

    const getFlowAlignment = (direction: TextDirection) =>
      direction === 'rtl' ? AlignmentType.RIGHT : AlignmentType.LEFT

    const getParagraphAlignment = (
      direction: TextDirection,
      shouldCenter = false,
    ) => (shouldCenter ? AlignmentType.CENTER : getFlowAlignment(direction))

    const withParagraphDirection = (
      options: Record<string, any>,
      direction: TextDirection,
    ) => ({
      ...options,
      ...(direction === 'rtl' ? { bidirectional: true } : {}),
    })

    const createDirectionalTextRun = (
      text: string,
      options?: import('docx').IRunOptions,
      contextDirection: TextDirection = 'ltr',
      runDirection?: TextDirection,
    ) => {
      const resolvedDirection =
        runDirection ?? resolveTextDirection(text, contextDirection)

      return new TextRun({
        text,
        ...options,
        ...(resolvedDirection === 'rtl' && options?.rightToLeft === undefined
          ? { rightToLeft: true }
          : {}),
      })
    }

    const getHeadingLevelValue = (depth: number) => {
      switch (depth) {
        case 1:
          return HeadingLevel.HEADING_1
        case 2:
          return HeadingLevel.HEADING_2
        case 3:
          return HeadingLevel.HEADING_3
        case 4:
          return HeadingLevel.HEADING_4
        case 5:
          return HeadingLevel.HEADING_5
        case 6:
        default:
          return HeadingLevel.HEADING_6
      }
    }
    const createSpacerParagraph = (spacing: {
      before?: number
      after?: number
    }) => {
      const spacer = new Paragraph({
        children: [new TextRun(' ')],
        spacing,
      })
      ;(spacer as any).__isSpacerParagraph = true
      return spacer
    }

    const isSpacerParagraph = (value: any) =>
      Boolean(value && (value as any).__isSpacerParagraph)

    const appendBlankParagraphs = (count: number) => {
      for (let i = 0; i < count; i += 1) {
        elements.push(createSpacerParagraph({ after: BLANK_LINE_SPACING }))
      }
    }

    const pushTableWithSpacing = (
      table: import('docx').Table,
      options?: { after?: boolean },
    ) => {
      if (
        elements.length > 0 &&
        !isSpacerParagraph(elements[elements.length - 1])
      ) {
        elements.push(createSpacerParagraph({ after: TABLE_BLOCK_SPACING }))
      }
      elements.push(table)
      if (options?.after !== false) {
        elements.push(createSpacerParagraph({ before: TABLE_BLOCK_SPACING }))
      }
    }

    const resolveMathmlConverter = (module: MathmlConverterModule | null) => {
      if (!module) return null
      const candidates = [
        (module as any).mml2omml,
        (module as any).mathml2omml,
        (module as any).convert,
        (module as any).convertMathml,
        (module as any).convertMathML,
        (module as any).default,
        module,
      ].filter((fn) => typeof fn === 'function')
      return candidates[0] as ((latex: string, opts?: any) => any) | null
    }

    const normalizeAndValidateOmml = (latex: string, omml: string) => {
      const normalizedOmml = normalizeOmmlForWps(omml)
      const reasons = getWpsUnsafeOmmlReasons(latex, normalizedOmml)
      if (reasons.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Falling back to math image for WPS compatibility', {
            reasons,
            latex,
          })
        }
        return null
      }
      return normalizedOmml
    }

    const convertLatexToOmml = async (latex: string, displayMode: boolean) => {
      try {
        const katex = await loadKatex()
        if (!katex?.renderToString) return null
        const mathml = normalizeKatexMathml(
          katex.renderToString(latex, {
            displayMode,
            throwOnError: false,
            output: 'mathml',
          }),
        )
        if (!mathml) return null

        const converterModule = await loadMathmlConverter()
        const converter = resolveMathmlConverter(converterModule)
        if (!converter) return null

        const result = await converter(mathml)
        if (typeof result === 'string') {
          return normalizeAndValidateOmml(latex, result)
        }
        if (typeof result?.omml === 'string') {
          return normalizeAndValidateOmml(latex, result.omml)
        }
        if (typeof result?.ommlString === 'string') {
          return normalizeAndValidateOmml(latex, result.ommlString)
        }
        if (typeof result?.data === 'string') {
          return normalizeAndValidateOmml(latex, result.data)
        }
        if (result) return result
      } catch (err) {
        console.warn('Math conversion failed', err)
      }
      return null
    }

    const buildMathRunFromConversionResult = (
      conversionResult: any,
      displayMode: boolean,
    ) => {
      if (!conversionResult) return null
      if (typeof (conversionResult as any).prepForXml === 'function') {
        const mathComponent = conversionResult as import('docx').ParagraphChild
        ;(mathComponent as any).__isMathDisplay = displayMode
        return mathComponent
      }
      if (
        Array.isArray(conversionResult) &&
        conversionResult.every(
          (item) => item && typeof item.prepForXml === 'function',
        )
      ) {
        const mathComponent = new toolkit.docx.Math({
          children: conversionResult,
        })
        ;(mathComponent as any).__isMathDisplay = displayMode
        return mathComponent as import('docx').ParagraphChild
      }
      if (typeof conversionResult === 'string') {
        const mathComponent = buildOmmlComponent(conversionResult)
        if (mathComponent) {
          ;(mathComponent as any).__isMathDisplay = displayMode
          return mathComponent
        }
      }
      return null
    }

    const normalizeOmmlTag = (tagName: string) =>
      tagName.includes(':') ? tagName : `m:${tagName}`

    const normalizeOmmlAttr = (attrName: string) => {
      if (
        attrName === 'xmlns' ||
        attrName.startsWith('xmlns:') ||
        attrName.includes(':')
      ) {
        return attrName
      }
      return `m:${attrName}`
    }

    const buildXmlChildren = (element: Element) => {
      const children: any[] = []
      const attrs: Record<string, string> = {}
      Array.from(element.attributes).forEach((attr) => {
        if (attr.name.startsWith('xmlns')) return
        attrs[normalizeOmmlAttr(attr.name)] = attr.value
      })
      if (Object.keys(attrs).length > 0) {
        children.push({ _attr: attrs })
      }
      element.childNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element
          const tagName = normalizeOmmlTag(el.tagName)
          const nestedChildren = buildXmlChildren(el)
          children.push({
            [tagName]: nestedChildren.length > 0 ? nestedChildren : {},
          })
          return
        }
        if (
          node.nodeType === Node.TEXT_NODE ||
          node.nodeType === Node.CDATA_SECTION_NODE
        ) {
          const text = node.nodeValue ?? ''
          if (text !== '') {
            children.push(text)
          }
        }
      })
      return children
    }

    const buildOmmlComponent = (omml: string) => {
      if (typeof window === 'undefined' || !omml) return null
      const parser = new DOMParser()
      const doc = parser.parseFromString(omml, 'application/xml')
      if (!doc?.documentElement) return null
      if (doc.getElementsByTagName('parsererror').length > 0) return null
      let root: Element = doc.documentElement
      if (root.localName === 'oMathPara') {
        const mathChild = Array.from(root.childNodes).find(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node as Element).localName === 'oMath',
        ) as Element | undefined
        if (mathChild) {
          root = mathChild
        }
      }
      if (root.localName !== 'oMath') return null
      const tagName = normalizeOmmlTag(root.tagName)
      const OmmlComponent = toolkit.docx.XmlComponent as unknown as {
        new (name: string): { root: any[] }
      }
      const math = new OmmlComponent(tagName)
      ;(math as any).root = buildXmlChildren(root)
      return math as unknown as import('docx').ParagraphChild
    }

    const pushPlainTextRuns = (
      targetRuns: Array<import('docx').ParagraphChild>,
      value?: string,
      options?: import('docx').IRunOptions,
      contextDirection: TextDirection = 'ltr',
      runDirection?: TextDirection,
    ) => {
      if (typeof value !== 'string') return
      const segments = value.split(/\r?\n/)
      if (segments.length <= 1) {
        targetRuns.push(
          createDirectionalTextRun(
            value,
            options,
            contextDirection,
            runDirection,
          ),
        )
        return
      }

      if (segments[segments.length - 1] === '') {
        segments.pop()
      }

      segments.forEach((segment, index) => {
        targetRuns.push(
          createDirectionalTextRun(
            segment === '' ? ' ' : segment,
            {
              break: index === 0 ? undefined : 1,
              ...options,
            },
            contextDirection,
            runDirection,
          ),
        )
      })
    }

    // 将内联 token 转换为 TextRun/ImageRun 等
    const parseInlineTokens = async (
      inlineTokens: any[] | undefined,
      fallbackText?: string,
      options?: {
        direction?: TextDirection
        runOptions?: import('docx').IRunOptions
      },
    ) => {
      const runs: Array<import('docx').ParagraphChild> = []
      const contextDirection =
        options?.direction ?? resolveTextDirection(fallbackText)
      const inheritedRunOptions = options?.runOptions

      const mergeRunOptions = (
        runOptions?: import('docx').IRunOptions,
      ): import('docx').IRunOptions | undefined => {
        if (!inheritedRunOptions) return runOptions
        if (!runOptions) return inheritedRunOptions
        return { ...inheritedRunOptions, ...runOptions }
      }

      const appendPlainTextRuns = (
        value?: string,
        runOptions?: import('docx').IRunOptions,
        runDirection?: TextDirection,
      ) =>
        pushPlainTextRuns(
          runs,
          value,
          mergeRunOptions(runOptions),
          contextDirection,
          runDirection,
        )

      const appendTextRuns = async (
        value?: string,
        runOptions?: import('docx').IRunOptions,
        shouldEmojify = true,
        allowMath = true,
        runDirection?: TextDirection,
      ) => {
        if (typeof value !== 'string') return
        const normalized =
          shouldEmojify && typeof value === 'string'
            ? emojifyText(value)
            : value
        const segments: MathSplit[] = allowMath
          ? splitMathSegments(normalized)
          : [{ kind: 'text', value: normalized }]

        for (const segment of segments) {
          if (segment.kind === 'math') {
            const mathRun = await createMathRun(segment.value, segment.display)
            if (mathRun) {
              runs.push(mathRun)
              continue
            }
            appendPlainTextRuns(`$${segment.value}$`, runOptions, runDirection)
            continue
          }
          appendPlainTextRuns(segment.value, runOptions, runDirection)
        }
      }

      if (!inlineTokens || inlineTokens.length === 0) {
        if (fallbackText) {
          await appendTextRuns(fallbackText)
        }
        return runs
      }

      for (const t of inlineTokens) {
        switch (t.type) {
          case 'text':
            if (Array.isArray(t.tokens) && t.tokens.length > 0) {
              const nested = await parseInlineTokens(t.tokens, t.text, {
                direction: contextDirection,
                runOptions: inheritedRunOptions,
              })
              runs.push(...nested)
            } else {
              await appendTextRuns(t.text ?? t.raw ?? '')
            }
            break
          case 'strong': {
            const nested = await parseInlineTokens(t.tokens, t.text, {
              direction: contextDirection,
              runOptions: mergeRunOptions({ bold: true }),
            })
            runs.push(...nested)
            break
          }
          case 'em': {
            const nested = await parseInlineTokens(t.tokens, t.text, {
              direction: contextDirection,
              runOptions: mergeRunOptions({ italics: true }),
            })
            runs.push(...nested)
            break
          }
          case 'del': {
            const nested = await parseInlineTokens(t.tokens, t.text, {
              direction: contextDirection,
              runOptions: mergeRunOptions({ strike: true }),
            })
            runs.push(...nested)
            break
          }
          case 'codespan':
            await appendTextRuns(
              t.text ?? '',
              {
                font: 'Courier New',
                shading: {
                  type: ShadingType.CLEAR,
                  color: 'auto',
                  fill: 'F1F5F9',
                },
              },
              false,
              false,
              'ltr',
            )
            break
          case 'bookmark': {
            const children = await parseInlineTokens(t.tokens, t.text, {
              direction: contextDirection,
              runOptions: inheritedRunOptions,
            })
            runs.push(navigation.bookmark(t.bookmark, children))
            break
          }
          case 'footnote':
            runs.push(navigation.noteReference(t.noteId, t.referenceBookmark))
            break
          case 'link': {
            // 保留链接文字的格式，并写入真实的外部关系或文内书签。
            const nested = await parseInlineTokens(
              t.tokens,
              t.text ?? t.href ?? '',
              {
                direction: contextDirection,
                runOptions: mergeRunOptions({
                  underline: { type: 'single' as any },
                  color: '0563C1',
                }),
              },
            )
            if (!t.bookmark && !isSafeWordHyperlink(t.href ?? '')) {
              runs.push(...nested)
              break
            }
            // 链接文字中也可能含脚注，保留其独立跳转，避免嵌套超链接。
            let linkChildren: import('docx').ParagraphChild[] = []
            const flushLink = () => {
              if (!linkChildren.length) return
              runs.push(
                t.bookmark
                  ? new InternalHyperlink({
                      anchor: t.bookmark,
                      children: linkChildren,
                    })
                  : new ExternalHyperlink({
                      link: t.href,
                      children: linkChildren,
                    }),
              )
              linkChildren = []
            }
            for (const child of nested) {
              if (
                child instanceof Bookmark ||
                child instanceof InternalHyperlink ||
                child instanceof ExternalHyperlink
              ) {
                flushLink()
                runs.push(child)
              } else {
                linkChildren.push(child)
              }
            }
            flushLink()
            break
          }
          case 'math': {
            const mathRun = await createMathRun(
              t.text ?? '',
              Boolean(t.display),
            )
            if (mathRun) {
              runs.push(mathRun)
              break
            }
            const delimiter = t.display ? '$$' : '$'
            appendPlainTextRuns(`${delimiter}${t.text ?? ''}${delimiter}`)
            break
          }
          case 'html':
            await appendTextRuns(t.text ?? '', undefined, true, false)
            break
          case 'paragraph': {
            const nested = await parseInlineTokens(t.tokens, t.text, {
              direction: contextDirection,
              runOptions: inheritedRunOptions,
            })
            runs.push(...nested)
            break
          }
          case 'image':
            {
              const { href, text: alt } = t
              const imgRun = await createImageRunFromUrl(toolkit, href, alt)
              if (imgRun) runs.push(imgRun)
            }
            break
          default: {
            if (Array.isArray(t.tokens) && t.tokens.length > 0) {
              const nested = await parseInlineTokens(t.tokens, t.text, {
                direction: contextDirection,
                runOptions: inheritedRunOptions,
              })
              runs.push(...nested)
            } else if (t.text) {
              await appendTextRuns(t.text)
            }
            break
          }
          case 'br':
            // 强制换行
            runs.push(new TextRun({ break: 1 }))
            break
        }
      }
      return runs
    }

    const getInlineChildrenFromText = async (
      value: string,
      direction = resolveTextDirection(value),
    ) => {
      const parsedInlineMarkdown = await toolkit.parseMarkdownAst(value)
      const inlineTokens = adaptBlockAstNodes(
        parsedInlineMarkdown.root.children ?? [],
        parsedInlineMarkdown.markdown,
      ).flatMap((token) =>
        token.type === 'paragraph' && Array.isArray(token.tokens)
          ? token.tokens
          : [token],
      )
      return parseInlineTokens(inlineTokens, value, { direction })
    }

    const TABLE_MIN_COLUMN_WEIGHT = 6
    const TABLE_MAX_COLUMN_WEIGHT = 60

    const estimateTextWeight = (value: string) => {
      const normalized = value.replace(/\s+/g, ' ').trim()
      if (!normalized) return 1
      let weight = 0
      for (const ch of normalized) {
        weight += ch.charCodeAt(0) > 0x7f ? 2 : 1
      }
      const scaled = Math.ceil(Math.sqrt(weight) * 4)
      return Math.min(TABLE_MAX_COLUMN_WEIGHT, Math.max(1, scaled))
    }

    const computeColumnWidths = (rows: string[][], colCount: number) => {
      const safeColCount = Math.max(1, colCount)
      const weights = new Array(safeColCount).fill(TABLE_MIN_COLUMN_WEIGHT)
      rows.forEach((row) => {
        for (let i = 0; i < safeColCount; i += 1) {
          const cellText = row[i] ?? ''
          const weight = estimateTextWeight(cellText)
          if (weight > weights[i]) {
            weights[i] = weight
          }
        }
      })
      const totalWeight = weights.reduce((sum, weight) => sum + weight, 0) || 1
      const widths = weights.map((weight) =>
        Math.max(1, Math.round((weight / totalWeight) * CONTENT_WIDTH_TWIPS)),
      )
      const diff =
        CONTENT_WIDTH_TWIPS - widths.reduce((sum, width) => sum + width, 0)
      widths[widths.length - 1] = Math.max(1, widths[widths.length - 1] + diff)
      return widths
    }

    const htmlBlockTags = new Set([
      'P',
      'DIV',
      'TR',
      'LI',
      'TABLE',
      'THEAD',
      'TBODY',
      'TFOOT',
      'UL',
      'OL',
    ])

    const extractHtmlText = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent ?? ''
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return ''
      const el = node as Element
      if (el.tagName === 'BR') return '\n'

      let text = ''
      el.childNodes.forEach((child) => {
        text += extractHtmlText(child)
      })

      if (htmlBlockTags.has(el.tagName)) {
        text = text.replace(/\n+$/g, '')
        text += '\n'
      }

      return text
    }

    const normalizeHtmlText = (value: string) =>
      value
        .replace(/\r\n/g, '\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim()

    const splitHtmlTableParts = (value: string) => {
      if (typeof value !== 'string') {
        return [{ type: 'text', value: '' }] as Array<{
          type: 'text' | 'table'
          value: string
        }>
      }
      if (!value.toLowerCase().includes('<table')) {
        return [{ type: 'text', value }] as Array<{
          type: 'text' | 'table'
          value: string
        }>
      }
      const parts: Array<{ type: 'text' | 'table'; value: string }> = []
      const tablePattern = /<table[\s\S]*?<\/table>/gi
      let match: RegExpExecArray | null
      let lastIndex = 0

      while ((match = tablePattern.exec(value))) {
        if (match.index > lastIndex) {
          parts.push({
            type: 'text',
            value: value.slice(lastIndex, match.index),
          })
        }
        parts.push({ type: 'table', value: match[0] })
        lastIndex = match.index + match[0].length
      }

      if (parts.length === 0) {
        return [{ type: 'text', value }]
      }
      if (lastIndex < value.length) {
        parts.push({ type: 'text', value: value.slice(lastIndex) })
      }

      return parts
    }

    const buildParagraphFromText = async (value: string) => {
      const direction = resolveTextDirection(value)
      const children = await parseInlineTokens(undefined, value, { direction })
      if (children.length === 0) return null
      const hasDisplayMath = children.some(
        (c: any) => (c as any).__isMathDisplay,
      )
      return new Paragraph(
        withParagraphDirection(
          {
            children,
            alignment: getParagraphAlignment(direction, hasDisplayMath),
            spacing: { after: hasDisplayMath ? 160 : 120 },
          },
          direction,
        ),
      )
    }

    const appendHtmlTextSegment = async (htmlSegment: string) => {
      if (!htmlSegment || !htmlSegment.trim()) return false
      if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
        return false
      }
      const doc = new DOMParser().parseFromString(htmlSegment, 'text/html')
      const plainText = normalizeHtmlText(extractHtmlText(doc.body))
      if (!plainText) return false
      const paragraph = await buildParagraphFromText(plainText)
      if (!paragraph) return false
      elements.push(paragraph)
      return true
    }

    const parseHtmlTablesToDocx = async (
      html: string,
      options?: { indentTwips?: number },
    ) => {
      if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
        return [] as import('docx').Table[]
      }
      if (!html || !html.toLowerCase().includes('<table')) {
        return [] as import('docx').Table[]
      }

      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const tables = Array.from(doc.querySelectorAll('table'))
      if (tables.length === 0) return [] as import('docx').Table[]

      const getRowCells = (row: HTMLTableRowElement) =>
        Array.from(row.children).filter((cell) => {
          const tag = cell.tagName
          return tag === 'TD' || tag === 'TH'
        }) as HTMLTableCellElement[]

      const buildCell = async (
        cell: HTMLTableCellElement,
        isHeader: boolean,
        fallbackDirection: TextDirection,
      ) => {
        const rawText = normalizeHtmlText(extractHtmlText(cell))
        const direction = resolveTextDirection(rawText, fallbackDirection)
        if (!rawText) {
          return new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun(' ')],
                alignment: AlignmentType.LEFT,
              }),
            ],
          })
        }
        if (isHeader) {
          return new TableCell({
            children: [
              new Paragraph(
                withParagraphDirection(
                  {
                    children: [
                      createDirectionalTextRun(
                        emojifyText(rawText),
                        { bold: true },
                        direction,
                      ),
                    ],
                    alignment: getFlowAlignment(direction),
                  },
                  direction,
                ),
              ),
            ],
          })
        }
        const children = await parseInlineTokens(undefined, rawText, {
          direction,
        })
        return new TableCell({
          children: [
            new Paragraph(
              withParagraphDirection(
                {
                  children: children.length > 0 ? children : [new TextRun(' ')],
                  alignment: getFlowAlignment(direction),
                },
                direction,
              ),
            ),
          ],
        })
      }

      const buildRow = async (
        row: HTMLTableRowElement,
        isHeader: boolean,
        colCount: number,
        fallbackDirection: TextDirection,
      ) => {
        const cells = getRowCells(row)
        const builtCells = await Promise.all(
          cells.map((cell) => buildCell(cell, isHeader, fallbackDirection)),
        )
        while (builtCells.length < colCount) {
          builtCells.push(
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun(' ')],
                  alignment: AlignmentType.LEFT,
                }),
              ],
            }),
          )
        }
        return new TableRow({
          children: builtCells,
          tableHeader: isHeader,
        })
      }

      const docxTables: import('docx').Table[] = []

      for (const table of tables) {
        let headRows = table.tHead ? Array.from(table.tHead.rows) : []
        const footRows = table.tFoot ? Array.from(table.tFoot.rows) : []
        let bodyRows: HTMLTableRowElement[] = []

        if (table.tBodies.length > 0) {
          bodyRows = Array.from(table.tBodies).flatMap((section) =>
            Array.from(section.rows),
          )
        } else {
          bodyRows = Array.from(table.rows)
        }

        if (headRows.length === 0 && bodyRows.length > 0) {
          headRows = [bodyRows[0]]
          bodyRows = bodyRows.slice(1)
        } else if (headRows.length > 0 && table.tBodies.length === 0) {
          const headSet = new Set(headRows)
          bodyRows = bodyRows.filter((row) => !headSet.has(row))
        }

        if (footRows.length > 0) {
          bodyRows = [...bodyRows, ...footRows]
        }

        const rowsForCount = [...headRows, ...bodyRows]
        const colCount =
          Math.max(1, ...rowsForCount.map((row) => getRowCells(row).length)) ||
          1
        const rowTextMatrix = rowsForCount.map((row) =>
          getRowCells(row).map((cell) =>
            normalizeHtmlText(extractHtmlText(cell)),
          ),
        )
        const tableDirection = resolveTextDirection(
          rowTextMatrix.flat().join('\n'),
        )
        const columnWidths = computeColumnWidths(rowTextMatrix, colCount)
        const headerDocxRows = await Promise.all(
          headRows.map((row) => buildRow(row, true, colCount, tableDirection)),
        )
        const bodyDocxRows = await Promise.all(
          bodyRows.map((row) => buildRow(row, false, colCount, tableDirection)),
        )

        const tableDocx = new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          columnWidths,
          layout: TableLayoutType.FIXED,
          margins: { top: 80, bottom: 80, left: 120, right: 120 },
          indent: options?.indentTwips
            ? { size: options.indentTwips, type: WidthType.DXA }
            : undefined,
          rows: [...headerDocxRows, ...bodyDocxRows],
        })

        docxTables.push(tableDocx)
      }

      return docxTables
    }

    const MERMAID_MAX_WIDTH = CONTENT_WIDTH_PX // clamp to full doc body width
    const MERMAID_SCALE = 6 // 2x larger than before
    const MERMAID_EXPORT_SCALE = MERMAID_SCALE

    const createMermaidImageRun = async (
      code: string,
      maxWidth = MERMAID_MAX_WIDTH,
    ) => {
      const png = await renderMermaidToPng(code, maxWidth, MERMAID_EXPORT_SCALE)
      if (!png) return null
      const base64 = png.dataUrl.split(',')[1]
      if (!base64) return null
      const binary = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
      const baseWidth = png.width || maxWidth
      const displayWidth = Math.min(
        maxWidth,
        Math.round(baseWidth * MERMAID_SCALE),
      )
      const scale = displayWidth / Math.max(baseWidth, 1)
      const displayHeight = Math.max(
        1,
        Math.round((png.height || baseWidth) * scale),
      )
      return new ImageRun({
        data: binary,
        type: 'png',
        transformation: { width: displayWidth, height: displayHeight },
        altText: { name: 'mermaid diagram' },
      })
    }

    const createMathImageRun = async (latex: string, displayMode: boolean) => {
      const trimmed = latex.trim()
      if (!trimmed) return null
      const png = await renderLatexToPng(trimmed, displayMode)
      if (!png?.dataUrl) return null
      const base64 = png.dataUrl.split(',')[1]
      if (!base64) return null
      const binary = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
      const maxWidth = displayMode
        ? DISPLAY_MATH_MAX_WIDTH
        : INLINE_MATH_MAX_WIDTH
      const targetWidth = Math.min(maxWidth, png.width || maxWidth)
      const targetHeight = Math.max(
        1,
        Math.round(
          (png.height || targetWidth) *
            (targetWidth / Math.max(png.width || targetWidth, 1)),
        ),
      )
      const run = new toolkit.docx.ImageRun({
        data: binary,
        type: 'png',
        transformation: { width: targetWidth, height: targetHeight },
        altText: { name: 'formula' },
      })
      ;(run as any).__isMathDisplay = displayMode
      return run
    }

    const createMathRun = async (latex: string, displayMode: boolean) => {
      const trimmed = latex.trim()
      if (!trimmed) return null

      const conversionResult = await convertLatexToOmml(trimmed, displayMode)
      const mathComponent = buildMathRunFromConversionResult(
        conversionResult,
        displayMode,
      )
      if (mathComponent) return mathComponent

      const imageRun = await createMathImageRun(trimmed, displayMode)
      if (imageRun) return imageRun

      return null
    }

    const applyBlockquoteParagraphStyle = (
      options: Record<string, any>,
      quoteDepth = 0,
      direction: TextDirection = 'ltr',
    ) => {
      if (quoteDepth <= 0) return options
      const nextOptions = { ...options }
      const existingIndent = nextOptions.indent ?? {}
      const existingStartIndent =
        existingIndent.start ??
        (direction === 'rtl' ? existingIndent.right : existingIndent.left) ??
        0
      nextOptions.indent = {
        ...existingIndent,
        start: existingStartIndent + quoteDepth * BLOCKQUOTE_INDENT_TWIPS,
      }
      const borderSide = direction === 'rtl' ? 'right' : 'left'
      nextOptions.border = {
        ...(nextOptions.border ?? {}),
        [borderSide]: {
          style: BorderStyle.SINGLE,
          size: 12,
          color: 'BFBFBF',
          space: 200,
        },
      }
      return nextOptions
    }

    const pushParagraph = (
      options: Record<string, any>,
      quoteDepth = 0,
      direction: TextDirection = 'ltr',
    ) => {
      elements.push(
        new Paragraph(
          withParagraphDirection(
            applyBlockquoteParagraphStyle(options, quoteDepth, direction),
            direction,
          ),
        ),
      )
    }

    const renderList = async (
      listToken: WordMarkdownToken,
      level = 0,
      quoteDepth = 0,
    ): Promise<void> => {
      const isOrdered = Boolean(listToken.ordered)
      let index = listToken.start ?? 1

      for (const item of listToken.items ?? []) {
        const direction = resolveTextDirection(item.text)
        const indentStart = isOrdered
          ? getListIndentLeft(level)
          : getBulletListTextIndentLeft(level, documentSettings.fontSizePt)
        const hanging = getBulletListHanging(documentSettings.fontSizePt)
        const isTaskItem = typeof item.checked === 'boolean'
        const orderedPrefix = `${index}. ${
          isTaskItem ? `${item.checked ? '☑' : '☐'} ` : ''
        }`
        const bulletNumbering = {
          reference: isTaskItem
            ? item.checked
              ? LIST_CHECKED_NUMBERING_REFERENCE
              : LIST_UNCHECKED_NUMBERING_REFERENCE
            : LIST_BULLET_NUMBERING_REFERENCE,
          level,
        }
        let markerRendered = false

        const pushItemParagraph = (
          children: import('docx').ParagraphChild[],
          paragraphDirection: TextDirection = direction,
        ) => {
          const firstParagraph = !markerRendered
          const hasImage = children.some((child) => child instanceof ImageRun)
          const hasDisplayMath = children.some(
            (child: any) => child.__isMathDisplay,
          )
          pushParagraph(
            {
              children: [
                ...(firstParagraph && isOrdered
                  ? [
                      createDirectionalTextRun(
                        orderedPrefix,
                        undefined,
                        paragraphDirection,
                        paragraphDirection,
                      ),
                    ]
                  : []),
                ...children,
              ],
              numbering:
                firstParagraph && !isOrdered ? bulletNumbering : undefined,
              alignment: getParagraphAlignment(
                paragraphDirection,
                hasDisplayMath,
              ),
              spacing: {
                before: hasImage || hasDisplayMath ? 240 : undefined,
                after: hasDisplayMath ? 140 : 100,
              },
              // 首段标记悬挂在正文外，续段仅保留正文缩进，不重复编号。
              indent: {
                start: indentStart,
                ...(firstParagraph && !isOrdered ? { hanging } : {}),
              },
              ...(firstParagraph && !isOrdered
                ? {
                    tabStops: [
                      {
                        type: TabStopType.NUM,
                        position:
                          indentStart + quoteDepth * BLOCKQUOTE_INDENT_TWIPS,
                      },
                    ],
                  }
                : {}),
            },
            quoteDepth,
            paragraphDirection,
          )
          markerRendered = true
        }

        const ensureItemMarker = () => {
          if (!markerRendered) pushItemParagraph([new TextRun(' ')])
        }
        const itemBlocks: WordMarkdownToken[] = item.tokens?.length
          ? item.tokens
          : [{ type: 'paragraph', text: item.text ?? '' }]

        // 按 AST 的块顺序处理，段落、子列表和后续正文不能合并或重排。
        for (const block of itemBlocks) {
          if (block.type === 'space') continue
          if (block.type === 'list') {
            ensureItemMarker()
            await renderList(block, level + 1, quoteDepth)
            continue
          }

          const blockDirection = resolveTextDirection(block.text, direction)
          if (block.type === 'paragraph' || block.type === 'html') {
            const parts = splitHtmlTableParts(block.text ?? block.raw ?? '')
            if (parts.some((part) => part.type === 'table')) {
              // HTML 表格仅在当前块内切分，前后独立段落继续保留各自边界。
              for (const part of parts) {
                if (part.type === 'table') {
                  const tables = await parseHtmlTablesToDocx(part.value, {
                    indentTwips:
                      indentStart + quoteDepth * BLOCKQUOTE_INDENT_TWIPS,
                  })
                  if (tables.length) {
                    ensureItemMarker()
                    for (const table of tables) pushTableWithSpacing(table)
                    continue
                  }
                }
                if (!part.value.trim()) continue
                const partDirection = resolveTextDirection(
                  part.value,
                  blockDirection,
                )
                const children = await getInlineChildrenFromText(
                  part.value,
                  partDirection,
                )
                if (children.length) pushItemParagraph(children, partDirection)
              }
              continue
            }
          }

          if (block.type === 'math') {
            const mathRun = await createMathRun(block.text ?? '', true)
            ensureItemMarker()
            if (mathRun) {
              pushItemParagraph([mathRun], blockDirection)
            } else {
              pushItemParagraph(
                [
                  createDirectionalTextRun(
                    `$$${block.text ?? ''}$$`,
                    undefined,
                    blockDirection,
                  ),
                ],
                blockDirection,
              )
            }
            continue
          }
          if (block.type === 'code') {
            // 代码内容保持字面量，不送入内联公式识别。
            const children: import('docx').ParagraphChild[] = []
            pushPlainTextRuns(
              children,
              block.text,
              { font: 'Courier New' },
              blockDirection,
            )
            pushItemParagraph(children, blockDirection)
            continue
          }

          const children = await parseInlineTokens(block.tokens, block.text, {
            direction: blockDirection,
          })
          if (children.length || !markerRendered) {
            pushItemParagraph(children, blockDirection)
          }
        }
        ensureItemMarker()
        if (isOrdered) index += 1
      }
    }

    const renderBlockquote = async (blockquoteToken: any, quoteDepth = 1) => {
      const quoteTokens = Array.isArray(blockquoteToken.tokens)
        ? blockquoteToken.tokens
        : []

      if (quoteTokens.length === 0) {
        const direction = resolveTextDirection(blockquoteToken.text)
        const children = await parseInlineTokens(
          undefined,
          blockquoteToken.text,
          { direction },
        )
        if (children.length > 0) {
          const hasImage = children.some((c) => c instanceof ImageRun)
          pushParagraph(
            {
              children,
              alignment: getFlowAlignment(direction),
              spacing: { before: hasImage ? 240 : undefined, after: 120 },
            },
            quoteDepth,
            direction,
          )
        }
        return
      }

      for (const quoteToken of quoteTokens) {
        switch (quoteToken.type) {
          case 'paragraph': {
            const direction = resolveTextDirection(quoteToken.text)
            const children = await parseInlineTokens(
              quoteToken.tokens,
              quoteToken.text,
              { direction },
            )
            const hasImage = children.some((c) => c instanceof ImageRun)
            pushParagraph(
              {
                children,
                alignment: getFlowAlignment(direction),
                spacing: { before: hasImage ? 240 : undefined, after: 120 },
              },
              quoteDepth,
              direction,
            )
            break
          }
          case 'math': {
            const mathRun = await createMathRun(quoteToken.text ?? '', true)
            if (!mathRun) break
            pushParagraph(
              {
                children: [mathRun],
                alignment: AlignmentType.CENTER,
                spacing: { before: 240, after: 160 },
              },
              quoteDepth,
              'ltr',
            )
            break
          }
          case 'list':
            await renderList(quoteToken, 0, quoteDepth)
            break
          case 'blockquote':
            await renderBlockquote(quoteToken, quoteDepth + 1)
            break
          case 'space':
            break
          default: {
            if (!(quoteToken as any).text) break
            const direction = resolveTextDirection((quoteToken as any).text)
            const children = await parseInlineTokens(
              (quoteToken as any).tokens,
              (quoteToken as any).text,
              { direction },
            )
            if (children.length === 0) break
            pushParagraph(
              {
                children,
                alignment: getFlowAlignment(direction),
                spacing: { after: 120 },
              },
              quoteDepth,
              direction,
            )
          }
        }
      }
    }

    for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex += 1) {
      const token = tokens[tokenIndex]
      const nextToken =
        tokenIndex + 1 < tokens.length ? tokens[tokenIndex + 1] : null
      switch (token.type) {
        case 'heading': {
          const direction = resolveTextDirection(token.text)
          const children = await parseInlineTokens(token.tokens, token.text, {
            direction,
          })
          const hasImage = children.some((c) => c instanceof ImageRun)
          const hasDisplayMath = children.some(
            (c: any) => (c as any).__isMathDisplay,
          )
          elements.push(
            new Paragraph(
              withParagraphDirection(
                {
                  heading: getHeadingLevelValue(token.depth ?? 1),
                  children,
                  alignment:
                    token.depth === 1
                      ? AlignmentType.CENTER
                      : getFlowAlignment(direction),
                  spacing: {
                    before: hasImage || hasDisplayMath ? 240 : undefined,
                    after: hasDisplayMath ? 200 : 200,
                  },
                },
                direction,
              ),
            ),
          )
          break
        }
        case 'html': {
          const html = token.text ?? token.raw ?? ''
          const htmlParts = splitHtmlTableParts(html)
          const hasHtmlTable = htmlParts.some((part) => part.type === 'table')
          if (hasHtmlTable) {
            let renderedAny = false
            for (
              let partIndex = 0;
              partIndex < htmlParts.length;
              partIndex += 1
            ) {
              const part = htmlParts[partIndex]
              if (part.type === 'table') {
                const tables = await parseHtmlTablesToDocx(part.value)
                if (tables.length === 0) {
                  if (await appendHtmlTextSegment(part.value)) {
                    renderedAny = true
                  }
                  continue
                }
                const nextMeaningfulPart = htmlParts
                  .slice(partIndex + 1)
                  .find(
                    (nextPart) =>
                      nextPart.type === 'table' || nextPart.value.trim(),
                  )
                const shouldAddAfter =
                  nextMeaningfulPart?.type === 'text'
                    ? true
                    : nextMeaningfulPart?.type === 'table'
                      ? false
                      : nextToken?.type !== 'space'
                for (
                  let tableIndex = 0;
                  tableIndex < tables.length;
                  tableIndex += 1
                ) {
                  const isLastTable = tableIndex === tables.length - 1
                  pushTableWithSpacing(tables[tableIndex], {
                    after: isLastTable ? shouldAddAfter : true,
                  })
                }
                renderedAny = true
                continue
              }
              if (await appendHtmlTextSegment(part.value)) {
                renderedAny = true
              }
            }
            if (renderedAny) {
              break
            }
          }
          if (await appendHtmlTextSegment(html)) {
            break
          }
          break
        }
        case 'paragraph': {
          const rawText = token.text ?? ''
          const direction = resolveTextDirection(rawText)
          const inlineTableParts = splitHtmlTableParts(rawText)
          const hasInlineTable = inlineTableParts.some(
            (part) => part.type === 'table',
          )
          if (hasInlineTable) {
            let renderedAny = false
            for (const part of inlineTableParts) {
              if (part.type === 'text') {
                if (!part.value.trim()) continue
                const partDirection = resolveTextDirection(
                  part.value,
                  direction,
                )
                const children = await getInlineChildrenFromText(
                  part.value,
                  partDirection,
                )
                if (children.length === 0) continue
                const hasImage = children.some((c) => c instanceof ImageRun)
                const hasDisplayMath = children.some(
                  (c: any) => (c as any).__isMathDisplay,
                )
                const onlyImages =
                  children.length > 0 &&
                  children.every((c) => c instanceof ImageRun)
                elements.push(
                  new Paragraph(
                    withParagraphDirection(
                      {
                        children,
                        alignment: getParagraphAlignment(
                          partDirection,
                          onlyImages || hasDisplayMath,
                        ),
                        spacing: {
                          before: hasImage || hasDisplayMath ? 240 : undefined,
                          after: hasDisplayMath ? 160 : 120,
                        },
                      },
                      partDirection,
                    ),
                  ),
                )
                renderedAny = true
                continue
              }
              const tables = await parseHtmlTablesToDocx(part.value)
              if (tables.length === 0) {
                const partDirection = resolveTextDirection(
                  part.value,
                  direction,
                )
                const children = await getInlineChildrenFromText(
                  part.value,
                  partDirection,
                )
                if (children.length === 0) continue
                const hasImage = children.some((c) => c instanceof ImageRun)
                const hasDisplayMath = children.some(
                  (c: any) => (c as any).__isMathDisplay,
                )
                const onlyImages =
                  children.length > 0 &&
                  children.every((c) => c instanceof ImageRun)
                elements.push(
                  new Paragraph(
                    withParagraphDirection(
                      {
                        children,
                        alignment: getParagraphAlignment(
                          partDirection,
                          onlyImages || hasDisplayMath,
                        ),
                        spacing: {
                          before: hasImage || hasDisplayMath ? 240 : undefined,
                          after: hasDisplayMath ? 160 : 120,
                        },
                      },
                      partDirection,
                    ),
                  ),
                )
                renderedAny = true
                continue
              }
              for (const table of tables) {
                pushTableWithSpacing(table)
              }
              renderedAny = true
            }
            if (renderedAny) {
              break
            }
          }

          const children = await parseInlineTokens(token.tokens, token.text, {
            direction,
          })
          const hasImage = children.some((c) => c instanceof ImageRun)
          const hasDisplayMath = children.some(
            (c: any) => (c as any).__isMathDisplay,
          )
          const onlyImages =
            children.length > 0 && children.every((c) => c instanceof ImageRun)
          elements.push(
            new Paragraph(
              withParagraphDirection(
                {
                  children,
                  alignment: getParagraphAlignment(
                    direction,
                    onlyImages || hasDisplayMath,
                  ),
                  spacing: {
                    before: hasImage || hasDisplayMath ? 240 : undefined,
                    after: hasDisplayMath ? 160 : 120,
                  },
                },
                direction,
              ),
            ),
          )
          break
        }
        case 'math': {
          const mathRun = await createMathRun(token.text ?? '', true)
          if (!mathRun) {
            const direction = resolveTextDirection(token.text)
            elements.push(
              new Paragraph(
                withParagraphDirection(
                  {
                    children: [
                      createDirectionalTextRun(
                        `$$${token.text ?? ''}$$`,
                        undefined,
                        direction,
                      ),
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: { before: 240, after: 160 },
                  },
                  direction,
                ),
              ),
            )
            break
          }
          elements.push(
            new Paragraph({
              children: [mathRun],
              alignment: AlignmentType.CENTER,
              spacing: { before: 240, after: 160 },
            }),
          )
          break
        }
        case 'list': {
          await renderList(token, 0)
          break
        }
        case 'blockquote': {
          await renderBlockquote(token, 1)
          break
        }
        case 'code': {
          const codeText = token.text ?? ''
          const lang =
            typeof (token as any).lang === 'string'
              ? (token as any).lang.toLowerCase()
              : ''

          if (lang === 'mermaid') {
            const mermaidRun = await createMermaidImageRun(
              codeText,
              MERMAID_MAX_WIDTH,
            )
            if (mermaidRun) {
              elements.push(
                new Paragraph({
                  children: [mermaidRun],
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 160 },
                }),
              )
              break
            }
          }

          const normalized = codeText
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .replace(/\n+$/, '')
          const lines = normalized.split('\n')
          const PADDING = '    '
          const codeRuns =
            lines.length > 0
              ? lines.flatMap((line: string, index: number) => {
                  const paddedLine = `${PADDING}${
                    line.length === 0 ? ' ' : line
                  }${PADDING}`
                  if (index === 0) {
                    return [
                      new TextRun({
                        text: paddedLine,
                        font: 'Courier New',
                        size: 20,
                        color: 'FFFFFF',
                      }),
                    ]
                  }
                  return [
                    new TextRun({ break: 1 }),
                    new TextRun({
                      text: paddedLine,
                      font: 'Courier New',
                      size: 20,
                      color: 'FFFFFF',
                    }),
                  ]
                })
              : [
                  new TextRun({
                    text: `${PADDING} ${PADDING}`,
                    font: 'Courier New',
                    size: 20,
                    color: 'FFFFFF',
                  }),
                ]
          const paddedBlockRuns: import('docx').TextRun[] = [
            new TextRun({
              text: `${PADDING} ${PADDING}`,
              font: 'Courier New',
              size: 20,
              color: 'FFFFFF',
            }),
            new TextRun({ break: 1 }),
            ...codeRuns,
            new TextRun({ break: 1 }),
            new TextRun({
              text: `${PADDING} ${PADDING}`,
              font: 'Courier New',
              size: 20,
              color: 'FFFFFF',
            }),
          ]
          elements.push(
            new Paragraph({
              children: paddedBlockRuns,
              spacing: { before: 160, after: 200 },
              alignment: AlignmentType.LEFT,
              indent: { left: 240, right: 240 },
              shading: {
                type: ShadingType.CLEAR,
                color: 'auto',
                fill: '000000',
              },
            }),
          )
          break
        }
        case 'hr': {
          elements.push(
            new Paragraph({ thematicBreak: true, spacing: { after: 120 } }),
          )
          break
        }
        case 'space': {
          const raw = typeof token.raw === 'string' ? token.raw : ''
          const newlineCount = (raw.match(/\n/g) ?? []).length
          const blankLines = Math.max(1, newlineCount - 1)
          appendBlankParagraphs(blankLines)
          break
        }
        case 'table': {
          const headerTextRow = (token.header ?? []).map(
            (cell: any) => cell?.text ?? '',
          )
          const bodyTextRows = (token.rows ?? []).map((row: any[]) =>
            row.map((cell: any) => cell?.text ?? ''),
          )
          const tableDirection = resolveTextDirection(
            [headerTextRow, ...bodyTextRows].flat().join('\n'),
          )
          const headerCells = await Promise.all(
            (token.header ?? []).map(async (cell: any, columnIndex: number) => {
              const cellText = cell.text ?? ''
              const direction = resolveTextDirection(cellText, tableDirection)
              const children = await parseInlineTokens(
                Array.isArray(cell.tokens)
                  ? cell.tokens.filter((t: any) => t.type !== 'image')
                  : undefined,
                cellText,
                { direction, runOptions: { bold: true } },
              )
              return new TableCell({
                children: [
                  new Paragraph(
                    withParagraphDirection(
                      {
                        children,
                        alignment: getWordTableAlignment(
                          token.align?.[columnIndex],
                          direction,
                          AlignmentType,
                        ),
                      },
                      direction,
                    ),
                  ),
                ],
              })
            }),
          )
          const headerRow = new TableRow({
            tableHeader: true,
            children: headerCells,
          })

          const rows = await Promise.all(
            (token.rows ?? []).map(async (row: any[]) => {
              const cells = await Promise.all(
                row.map(async (cell: any, columnIndex: number) => {
                  const cellText = cell.text ?? ''
                  const direction = resolveTextDirection(
                    cellText,
                    tableDirection,
                  )
                  const children = await parseInlineTokens(
                    Array.isArray(cell.tokens)
                      ? cell.tokens.filter((t: any) => t.type !== 'image')
                      : undefined,
                    cellText,
                    { direction },
                  )

                  return new TableCell({
                    children: [
                      new Paragraph(
                        withParagraphDirection(
                          {
                            children,
                            alignment: getWordTableAlignment(
                              token.align?.[columnIndex],
                              direction,
                              AlignmentType,
                            ),
                          },
                          direction,
                        ),
                      ),
                    ],
                  })
                }),
              )

              return new TableRow({
                children: cells,
              })
            }),
          )

          const colCount =
            Math.max(
              token.header?.length ?? 0,
              ...(token.rows ?? []).map((r: any[]) => r.length),
            ) || 1
          const columnWidths = computeColumnWidths(
            [headerTextRow, ...bodyTextRows],
            colCount,
          )

          const table = new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            columnWidths,
            layout: TableLayoutType.FIXED,
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            rows: [headerRow, ...rows],
          })
          pushTableWithSpacing(table)
          break
        }
        default: {
          // 兜底：如果 token 有文本则作为普通段落
          if ((token as any).text) {
            const direction = resolveTextDirection((token as any).text)
            const children = await parseInlineTokens(
              (token as any).tokens,
              (token as any).text,
              { direction },
            )
            const hasDisplayMath = children.some(
              (c: any) => (c as any).__isMathDisplay,
            )
            elements.push(
              new Paragraph(
                withParagraphDirection(
                  {
                    children,
                    alignment: getParagraphAlignment(direction, hasDisplayMath),
                    spacing: { after: hasDisplayMath ? 160 : 120 },
                  },
                  direction,
                ),
              ),
            )
          }
        }
      }
    }

    return elements
  }

  // 转换并下载Word文档
  const convertToWord = async (markdownValue = markdownContent) => {
    if (!markdownValue.trim()) {
      alert(t('errors.noContent'))
      return
    }

    trackGtagEvent('download_click', {
      event_category: 'download',
      event_label: 'md_docx_button',
    })
    trackPlausibleEvent('download_click', {
      event_category: 'download',
      event_label: 'md_docx_button',
    })

    setIsConverting(true)
    try {
      await yieldToBrowser()
      const toolkit = await loadConversionToolkit()
      if (typeof toolkit?.parseMarkdownAst !== 'function') {
        throw new Error('Markdown parser failed to load')
      }
      if (typeof toolkit?.saveAs !== 'function') {
        throw new Error('Download helper failed to load')
      }
      const { Document, Packer, AlignmentType, LevelFormat, LevelSuffix } =
        toolkit.docx
      const parsedMarkdown = await toolkit.parseMarkdownAst(markdownValue)
      const references = prepareWordReferences(parsedMarkdown)
      const navigation = createWordNavigation(toolkit.docx)
      const elements = await parseMarkdownToWordElements(
        markdownValue,
        toolkit,
        documentSettings,
        references.body,
        navigation,
      )
      if (references.notes.size > 0) {
        const notesHeading: ParsedMarkdownAst = {
          markdown: '',
          root: {
            type: 'root',
            children: [
              { type: 'thematicBreak' },
              {
                type: 'heading',
                depth: 2,
                children: [{ type: 'text', value: t('notesTitle') }],
              },
            ],
          },
        }
        elements.push(
          ...(await parseMarkdownToWordElements(
            '',
            toolkit,
            documentSettings,
            notesHeading,
            navigation,
          )),
        )
        for (const note of Array.from(references.notes.values())) {
          elements.push(
            ...(await parseMarkdownToWordElements(
              markdownValue,
              toolkit,
              documentSettings,
              note,
              navigation,
            )),
          )
        }
      }

      const doc = new Document({
        styles: {
          default: {
            document: {
              run: { size: toHalfPoints(documentSettings.fontSizePt) },
              paragraph: { alignment: AlignmentType.LEFT },
            },
            heading1: {
              run: {
                color: '000000',
                bold: true,
                size: getHeadingRunSize(
                  documentSettings.fontSizePt,
                  'heading1',
                ),
              },
            },
            heading2: {
              run: {
                color: '000000',
                bold: true,
                size: getHeadingRunSize(
                  documentSettings.fontSizePt,
                  'heading2',
                ),
              },
            },
            heading3: {
              run: {
                color: '000000',
                bold: true,
                size: getHeadingRunSize(
                  documentSettings.fontSizePt,
                  'heading3',
                ),
              },
            },
            heading4: {
              run: {
                color: '000000',
                bold: true,
                size: getHeadingRunSize(
                  documentSettings.fontSizePt,
                  'heading4',
                ),
              },
            },
            heading5: {
              run: {
                color: '000000',
                bold: true,
                size: getHeadingRunSize(
                  documentSettings.fontSizePt,
                  'heading5',
                ),
              },
            },
            heading6: {
              run: {
                color: '000000',
                bold: true,
                size: getHeadingRunSize(
                  documentSettings.fontSizePt,
                  'heading6',
                ),
              },
            },
          },
        },
        numbering: {
          config: [
            {
              reference: LIST_BULLET_NUMBERING_REFERENCE,
              marker: (level: number) =>
                level % 3 === 1 ? '○' : level % 3 === 2 ? '■' : '●',
            },
            {
              reference: LIST_CHECKED_NUMBERING_REFERENCE,
              marker: () => '☑',
            },
            {
              reference: LIST_UNCHECKED_NUMBERING_REFERENCE,
              marker: () => '☐',
            },
          ].map(({ reference, marker }) => ({
            reference,
            levels: Array.from({ length: 9 }, (_, level) => ({
              level,
              format: LevelFormat.BULLET,
              text: marker(level),
              alignment: AlignmentType.START,
              suffix: LevelSuffix.TAB,
              style: {
                paragraph: {
                  indent: {
                    start: getBulletListTextIndentLeft(
                      level,
                      documentSettings.fontSizePt,
                    ),
                    hanging: getBulletListHanging(documentSettings.fontSizePt),
                  },
                },
              },
            })),
          })),
        },
        sections: [
          {
            properties: {},
            children: elements,
          },
        ],
      })

      const blob = await Packer.toBlob(doc)
      const outputFileName = getWordExportFileName(parsedMarkdown)

      toolkit.saveAs(blob, outputFileName)
    } catch (error) {
      console.error(t('errors.conversionFailed'), error)
      alert(t('errors.formatError'))
    } finally {
      setIsConverting(false)
    }
  }

  const handleConvertToWord = (markdownValue?: string) => {
    const runConvert = () => convertToWord(markdownValue)
    if (!guardDownload(runConvert)) return
    runConvert()
  }

  const handleConvertToWordClick = () => {
    handleConvertToWord()
  }

  // 下载并校验图片，使用真实格式写入 Word。
  const createImageRunFromUrl = async (
    toolkit: ConversionToolkit,
    url?: string,
    alt?: string,
  ) => {
    if (!url) return null
    const { ImageRun, TextRun } = toolkit.docx
    try {
      const image = await loadWordImage(url)
      const width = Math.round(6.5 * 96 * 0.8)
      return new ImageRun({
        data: image.data,
        type: image.type,
        transformation: {
          width,
          height: Math.max(1, Math.round((image.height * width) / image.width)),
        },
        altText: { name: alt ?? 'image' },
      })
    } catch {
      // 单张图片失败时保留说明和原链接，其他正文及图片继续导出。
      const label = t('errors.imageUnavailable')
      const description = alt ? `: ${emojifyText(alt)}` : ''
      const link = url.startsWith('data:') ? '' : ` ${url}`
      return new TextRun(`[${label}${description}]${link}`)
    }
  }

  // 编辑器滚动联动预览区域
  return (
    <>
      <MarkdownConverterShell
        markdownContent={markdownContent}
        exampleMarkdown={exampleMarkdown}
        onMarkdownChange={setMarkdownContent}
        onLoadExample={() => setMarkdownContent(exampleMarkdown)}
        dropzone={dropzone}
        uploadState={uploadState}
        uploadAriaLabel={t('upload.title')}
        uploadCopy={{
          subtitle: t('upload.title'),
          dragActive: t('upload.dragActive'),
          dragInactive: t('upload.dragInactive'),
          fileConstraints: t('upload.fileConstraints'),
        }}
        headings={{
          upload: {
            label: t('upload.mainTitle'),
            icon: <FileText className="mr-2 h-5 w-5" />,
            className: 'text-base font-semibold sm:text-lg',
          },
          editor: {
            label: t('editor.title'),
            icon: <FileText className="h-5 w-5" />,
            className: 'text-base font-semibold sm:text-lg',
          },
          preview: {
            label: t('preview.title'),
            icon: <Eye className="h-5 w-5" />,
            className: 'text-base font-semibold sm:text-lg',
          },
        }}
        loadExampleLabel={t('editor.loadExample')}
        lineCountLabel={tc('actions.lines') || 'lines'}
        uploadDropIcon={
          <Upload className="mx-auto mb-2 text-muted-foreground" size={36} />
        }
        previewPlaceholder={t('preview.placeholder')}
        previewExportSettings={documentSettings}
        previewToolbarActions={
          <DocumentExportSettingsButton
            settings={documentSettings}
            onChange={setDocumentSettings}
          />
        }
        onEnterFullscreen={enterFullscreen}
        onConvert={handleConvertToWordClick}
        isConverting={isConverting}
        convertLabels={{
          idle: t('conversion.downloadWord'),
          busy: t('conversion.converting'),
        }}
        fullscreenViewer={
          isFullscreenReady ? (
            <LazyMarkdownFullscreenViewer
              open={isFullscreen}
              onClose={exitFullscreen}
              markdown={markdownContent}
              fileName={fileName || 'markdown-to-word'}
              onSave={(newMd) => setMarkdownContent(newMd)}
              onDownload={handleConvertToWord}
              allowEdit
              downloadDisabled={isConverting}
              previewExportSettings={documentSettings}
              labels={{
                title: 'Markdown To Word - Fullscreen',
                source: tc('actions.source') || 'Source',
                preview: tc('actions.preview') || 'Preview',
                mobileToggleToPreview: tc('actions.preview') || 'Preview',
                mobileToggleToSource: tc('actions.source') || 'Source',
                copied: tc('actions.copied') || 'Copied!',
                copy: tc('actions.copy') || 'Copy',
                download: tc('actions.download') || 'Download',
                editing: tc('actions.editing') || 'Editing',
                live: tc('actions.live') || 'Live',
                linesSuffix: tc('actions.lines') || 'lines',
                exit: tc('fullscreen.exitFullscreen') || 'Exit',
              }}
            />
          ) : null
        }
        sourceScrollRef={sourceScrollRef}
        previewScrollRef={previewScrollRef}
        onSourceScroll={handleSourceScroll}
        actionHelperText={
          <div className="text-center text-sm text-muted-foreground">
            {t.rich('conversion.pasteToWordHelper', {
              link: (chunks) => (
                <Link
                  href={localizedHref}
                  className="font-medium underline underline-offset-4 transition-colors hover:text-primary"
                >
                  {chunks}
                </Link>
              ),
            })}
          </div>
        }
      />
      <InAppDownloadModal
        open={isInAppModalOpen}
        onClose={closeInAppModal}
        onContinue={continueWithDownload}
        appName={inAppInfo?.appName}
      />
    </>
  )
}
